import { useEffect, useRef, useState } from "react";
import {
  IARCarSegmentGroup,
  IARRowsPerGroup,
} from "../types/ISingleLocationAvailabilityReport";

/**
 * CR LPP-20500 (drilldown scalabile) — loader del drill-down (carSegment → carClass → ACRISS)
 * di TUTTI i custom group di un livello (DMS, regione o totale). Ritorna i gruppi con
 * `carSegments` valorizzato, oppure `null` in caso di errore (il chiamante ha già mostrato
 * l'errore all'utente e il livello resta "non caricato", quindi ritentabile).
 */
export type DrillDownLoader = () => Promise<IARRowsPerGroup[] | null>;

/**
 * Convenzione del BE (`includeDrillDown` = NONE / TOTAL_ONLY): `carSegments` omesso (null/undefined)
 * significa "drill-down NON caricato", mentre una lista (anche vuota) significa "caricato". Il drill-down
 * si carica alla prima espansione di un custom group del livello e vale per tutti i custom group del
 * livello (una sola chiamata). I dati restano in cache finché non cambia `resetKey` (nuova ricerca).
 */
export const useLazyDrillDown = (
  onLoad: DrillDownLoader | undefined,
  resetKey: unknown
) => {
  const [segmentsByGroup, setSegmentsByGroup] = useState<Map<
    string,
    IARCarSegmentGroup[]
  > | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Token incrementato a ogni reset: scarta le risposte di caricamenti avviati su dati precedenti.
  const token = useRef(0);
  const inFlight = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    token.current += 1;
    inFlight.current = null;
    setSegmentsByGroup(null);
    setLoading(false);
  }, [resetKey]);

  /** Restituisce il gruppo con i `carSegments` caricati (se disponibili). */
  const resolveGroup = <T extends IARRowsPerGroup>(group: T): T => {
    if (group.carSegments != null || segmentsByGroup === null) return group;
    return { ...group, carSegments: segmentsByGroup.get(group.group) ?? [] };
  };

  /** True se il drill-down del gruppo è ancora da caricare (e un loader è disponibile). */
  const needsLoad = (resolved: IARRowsPerGroup): boolean =>
    !!onLoad && resolved.carSegments == null;

  /** Carica il drill-down del livello. Una sola richiesta alla volta; true se caricato. */
  const load = (): Promise<boolean> => {
    if (!onLoad) return Promise.resolve(false);
    if (inFlight.current) return inFlight.current;

    const myToken = token.current;
    setLoading(true);
    const promise = onLoad()
      .then((groups) => {
        if (myToken !== token.current || groups === null) return false;
        const map = new Map<string, IARCarSegmentGroup[]>();
        groups.forEach((g) => map.set(g.group, g.carSegments ?? []));
        setSegmentsByGroup(map);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        if (myToken === token.current) {
          inFlight.current = null;
          setLoading(false);
        }
      });
    inFlight.current = promise;
    return promise;
  };

  return { resolveGroup, needsLoad, load, loading, segmentsByGroup };
};
